import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "@/api/axios";
import axios from "axios";
import type { User } from "@/types/auth";

const rootApi = axios.create({
    baseURL: "/",
    withCredentials: true,
    withXSRFToken: true,
});

export const useAuthStore = defineStore("auth", () => {
    const user = ref<User | null>(null);
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const isInitialized = ref(false);

    const isAuthenticated = computed(() => !!user.value);
    const userRoles = computed(() => user.value?.roles || []);
    const userPermissions = computed(() => {
        if (!user.value?.roles) {
            return [];
        }

        const rolePermissions = user.value.roles.flatMap((role: any) => {
            return role.permissions || [];
        });

        const directPermissions = user.value.permissions || [];

        const allPermissions = [...directPermissions, ...rolePermissions];

        return allPermissions;
    });
    const userName = computed(() => user.value?.name || "");
    const userEmail = computed(() => user.value?.email || "");

    const setUser = (newUser: User | null) => {
        user.value = newUser;
        if (newUser) {
            localStorage.setItem("auth_user", JSON.stringify(newUser));
        } else {
            localStorage.removeItem("auth_user");
        }
    };

    const setLoading = (loading: boolean) => {
        isLoading.value = loading;
    };

    const setError = (newError: string | null) => {
        error.value = newError;
    };

    const login = async (credentials: {
        email: string;
        password: string;
        remember?: boolean;
    }) => {
        try {
            setLoading(true);
            setError(null);

            await rootApi.get("/sanctum/csrf-cookie");

            const response = await api.post("/v1/login", credentials);

            if (response.data.success) {
                const { user: userData } = response.data.data;

                setUser(userData);
                await fetchUser();

                return { success: true, data: user.value };
            } else {
                throw new Error(response.data.message || "Login failed");
            }
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.message || err.message || "Login failed";
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoading(true);

            await api.post("/v1/logout");
        } catch (err: any) {
            console.error("Logout error:", err);
        } finally {
            setUser(null);
            setError(null);
            setLoading(false);
        }
    };

    const fetchUser = async () => {
        try {
            setLoading(true);

            const [userResponse, profileResponse] = await Promise.all([
                api.get("/v1/user"),
                api.get("/v1/profile"),
            ]);

            if (userResponse.data.success && profileResponse.data.success) {
                const userData = {
                    ...userResponse.data.data.user,
                    profile: profileResponse.data.data.profile,
                };
                setUser(userData);
                return userData;
            } else {
                throw new Error(
                    userResponse.data.message || "Failed to fetch user",
                );
            }
        } catch (err: any) {
            console.error("Fetch user error:", err);
            if (err.response?.status === 401) {
                setUser(null);
            }
            return null;
        } finally {
            setLoading(false);
        }
    };

    const hasRole = (role: string): boolean => {
        return userRoles.value.some((r: any) => r.name === role);
    };

    const hasPermission = (permission: string): boolean => {
        return userPermissions.value.some((p: any) => p.name === permission);
    };

    const hasAnyRole = (roles: string[]): boolean => {
        return roles.some((role) => hasRole(role));
    };

    const hasAnyPermission = (permissions: string[]): boolean => {
        return permissions.some((permission) => hasPermission(permission));
    };

    const initializeAuth = () => {
        const storedUser = localStorage.getItem("auth_user");

        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
                fetchUser().finally(() => {
                    isInitialized.value = true;
                });
            } catch (err) {
                console.error("Error parsing stored user:", err);
                localStorage.removeItem("auth_user");
                isInitialized.value = true;
            }
        } else {
            isInitialized.value = true;
        }
    };

    const clearAuth = () => {
        setUser(null);
        setError(null);
        setLoading(false);
    };

    return {
        user,
        isLoading,
        error,
        isInitialized,

        isAuthenticated,
        userRoles,
        userPermissions,
        userName,
        userEmail,

        login,
        logout,
        fetchUser,
        setUser,
        setLoading,
        setError,
        hasRole,
        hasPermission,
        hasAnyRole,
        hasAnyPermission,
        initializeAuth,
        clearAuth,
    };
});
